
import type * as THREE_NS from 'three';

/**
 * Create a full-screen shader sky background
 * Replaces the physical sky sphere for a more expansive, procedural environment
 */
export function createSkyShader(scene: THREE_NS.Scene, THREE: typeof THREE_NS): THREE_NS.Mesh {
  // Full-screen quad geometry (renders behind everything)
  // We'll use a large sphere instead of a quad to make camera rotation easier, 
  // but render it on the "background" or with depthWrite: false
  const geometry = new THREE.SphereGeometry(5000, 32, 32);

  const vertexShader = `
    varying vec3 vWorldPosition;
    varying vec2 vUv;
    
    void main() {
      vec4 worldPosition = modelMatrix * vec4(position, 1.0);
      vWorldPosition = worldPosition.xyz;
      vUv = uv;
      gl_Position = projectionMatrix * viewMatrix * worldPosition;
    }
  `;

  const fragmentShader = `
    uniform vec3 topColor;
    uniform vec3 bottomColor;
    uniform float offset;
    uniform float exponent;
    uniform float time;
    uniform float clouds;
    
    varying vec3 vWorldPosition;
    varying vec2 vUv;

    // Simplex 2D noise
    vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x, 289.0); }

    float snoise(vec2 v){
      const vec4 C = vec4(0.211324865405187, 0.366025403784439,
               -0.577350269189626, 0.024390243902439);
      vec2 i  = floor(v + dot(v, C.yy) );
      vec2 x0 = v -   i + dot(i, C.xx);
      vec2 i1;
      i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
      vec4 x12 = x0.xyxy + C.xxzz;
      x12.xy -= i1;
      i = mod(i, 289.0);
      vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
      + i.x + vec3(0.0, i1.x, 1.0 ));
      vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
      m = m*m ;
      m = m*m ;
      vec3 x = 2.0 * fract(p * C.www) - 1.0;
      vec3 h = abs(x) - 0.5;
      vec3 ox = floor(x + 0.5);
      vec3 a0 = x - ox;
      m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
      vec3 g;
      g.x  = a0.x  * x0.x  + h.x  * x0.y;
      g.yz = a0.yz * x12.xz + h.yz * x12.yw;
      return 130.0 * dot(m, g);
    }

    void main() {
      float h = normalize(vWorldPosition + offset).y;
      
      // Basic gradient
    vec3 color = mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0));
    
    // Removed clouds logic per user request
    // Cloud logic was here...
    
    gl_FragColor = vec4(color, 1.0);
    }
  `;

  const uniforms = {
    topColor: { value: new THREE.Color(0x0077ff) },
    bottomColor: { value: new THREE.Color(0xffffff) },
    offset: { value: 33 },
    exponent: { value: 0.6 },
    time: { value: 0 },
    clouds: { value: 0.0 } // 0 = off, 1 = on
  };

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms,
    side: THREE.BackSide,
    depthWrite: false, // Don't write to depth buffer so it acts as background
    fog: false // Ignore fog
  });

  const sky = new THREE.Mesh(geometry, material);
  scene.add(sky);

  return sky;
}

/**
 * Updates the sky shader uniforms
 */
export function updateSkyShader(sky: THREE_NS.Mesh, time: number, theme: 'dark' | 'day'): void {
  const material = sky.material as THREE_NS.ShaderMaterial;
  material.uniforms.time.value = time;

  // You could lerp these values for smooth transitions
  if (theme === 'day') {
    // Day sky: Blue to White gradient with clouds
    material.uniforms.topColor.value.setHex(0x0077ff);
    material.uniforms.bottomColor.value.setHex(0xffffff);
    material.uniforms.clouds.value = 1.0;
    material.uniforms.exponent.value = 0.6;
  } else {
    // Dark/Night sky: Black/Dark Blue
    // Or we could just hide it and let the clear color handle it, 
    // but having a dark gradient is nice too.
    material.uniforms.topColor.value.setHex(0x000000);
    material.uniforms.bottomColor.value.setHex(0x050510); // Very deep blue horizon
    material.uniforms.clouds.value = 0.0;
    material.uniforms.exponent.value = 0.8;
  }
}

